import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Select, Space, Spin, Switch, Table, Tag, Tooltip, Typography, message } from 'antd';
import {
    CheckCircleOutlined,
    DatabaseOutlined,
    ExclamationCircleOutlined,
    FileSearchOutlined,
    MinusCircleOutlined,
    ReloadOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import settlementApi from '../../../../api/settlementApi';

dayjs.extend(utc);

const { Text } = Typography;

const STATUS_META = {
    no_data: { label: '无数据', color: 'default', icon: <MinusCircleOutlined /> },
    incomplete: { label: '数据不全', color: 'warning', icon: <ExclamationCircleOutlined /> },
    anomaly: { label: '待核查', color: 'error', icon: <WarningOutlined /> },
    ok: { label: '正常', color: 'success', icon: <CheckCircleOutlined /> },
};

const formatNumber = (value, digits = 2) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
    return Number(value).toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
};

const formatTons = (kg, digits = 2) => {
    if (kg === null || kg === undefined || Number.isNaN(Number(kg))) return '-';
    return formatNumber(Number(kg) / 1000, digits);
};

const formatDate = (value) => {
    if (!value) return '-';
    const date = dayjs(value);
    return date.isValid() ? date.format('YYYY-MM-DD') : value;
};

const formatUtcTime = (value) => {
    if (!value) return '-';
    const date = dayjs.utc(value);
    return date.isValid() ? date.format('MM-DD HH:mm') : value;
};

const formatFullUtcTime = (value) => {
    if (!value) return '-';
    const date = dayjs.utc(value);
    return date.isValid() ? `${date.format('YYYY-MM-DD HH:mm')} UTC` : value;
};

const renderStatus = (status) => {
    const meta = STATUS_META[status] || STATUS_META.no_data;
    return (
        <Tag color={meta.color} icon={meta.icon} style={{ margin: 0 }}>
            {meta.label}
        </Tag>
    );
};

const renderReportCount = (count) => {
    if (!count) return <Text type="secondary">0</Text>;
    if (count > 1) return <Tag color="blue">多次({count})</Tag>;
    return <Tag color="green">1</Tag>;
};

const renderNode = (node) => (
    <Space direction="vertical" size={0} style={{ lineHeight: 1.35 }}>
        <Tooltip title={formatFullUtcTime(node?.time)}>
            <Text style={{ fontSize: 12 }}>{formatUtcTime(node?.time)}</Text>
        </Tooltip>
        <Text strong={node?.fuel_kg !== null && node?.fuel_kg !== undefined}>
            {formatTons(node?.fuel_kg, 2)}
        </Text>
    </Space>
);

// 后端字段单位是 kg/h，UI 展示成 t/h（吨/小时）
// 不改 API 契约：后端字段名 block_fuel_kg_per_h / flight_fuel_kg_per_h 保持
const renderTonsPerHour = (value) => {
    const numeric = Number(value);
    const isNegative = Number.isFinite(numeric) && numeric < 0;
    const tonsPerHour = Number.isFinite(numeric) ? numeric / 1000 : value;
    return (
        <Text strong style={{ color: isNegative ? '#cf1322' : undefined }}>
            {formatNumber(tonsPerHour, 2)}
        </Text>
    );
};

const FuelCoveragePanel = ({ filters, active }) => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
    const [sorter, setSorter] = useState({});
    // Switch 「仅显示数据完整的航班」默认开启，对应后端 status_filter='ok'。
    // 这是"视角偏好"——飞机/日期/排序变化时不重置，由 S4/FR-009 决定。
    const [onlyOk, setOnlyOk] = useState(true);

    // 本地筛选项：始发站 / 目的站 / 机型，与父级 dashboard 全局 filter 解耦
    const [departureAirport, setDepartureAirport] = useState(null);
    const [arrivalAirport, setArrivalAirport] = useState(null);
    const [aircraftTypes, setAircraftTypes] = useState([]);

    // 下拉选项（从后端 /fuel-coverage/filter-options 拉一次）
    const [filterOptions, setFilterOptions] = useState({
        departure_airports: [],
        arrival_airports: [],
        aircraft_types: [],
    });

    const filterSignature = useMemo(() => JSON.stringify({
        aircraft_reg: filters?.aircraft_reg || [],
        date_range: filters?.date_range || [],
        departureAirport,
        arrivalAirport,
        aircraftTypes,
    }), [filters, departureAirport, arrivalAirport, aircraftTypes]);

    const buildParams = useCallback((page, pageSize, nextSorter = sorter, nextOnlyOk = onlyOk) => {
        const params = {
            page,
            page_size: pageSize,
            status_filter: nextOnlyOk ? 'ok' : 'all',
        };
        if (filters?.date_range?.length === 2) {
            params.start_date = filters.date_range[0];
            params.end_date = filters.date_range[1];
        }
        if (filters?.aircraft_reg?.length > 0) {
            params.aircraft_reg = filters.aircraft_reg.join(',');
        }
        if (departureAirport) params.departure_airport = departureAirport;
        if (arrivalAirport) params.arrival_airport = arrivalAirport;
        if (aircraftTypes?.length > 0) params.aircraft_type = aircraftTypes.join(',');
        if (nextSorter?.field && nextSorter?.order) {
            params.sort_by = nextSorter.field;
            params.sort_order = nextSorter.order;
        }
        return params;
    }, [filters, sorter, onlyOk, departureAirport, arrivalAirport, aircraftTypes]);

    const loadData = useCallback(async ({
        page = pagination.current,
        pageSize = pagination.pageSize,
        nextSorter = sorter,
        nextOnlyOk = onlyOk,
    } = {}) => {
        if (!active) return;
        setLoading(true);
        try {
            const response = await settlementApi.fuelCoverage.getCoverage(buildParams(page, pageSize, nextSorter, nextOnlyOk));
            const payload = response?.data || {};
            setRows(payload.items || []);
            setPagination({
                current: payload.page || page,
                pageSize: payload.page_size || pageSize,
                total: payload.total || 0,
            });
            if (payload.warnings?.length > 0) {
                message.warning(payload.warnings.join('；'));
            }
        } catch (error) {
            console.error('加载燃油覆盖列表失败:', error);
            message.error('加载燃油覆盖列表失败');
            setRows([]);
            setPagination(prev => ({ ...prev, total: 0 }));
        } finally {
            setLoading(false);
        }
    }, [active, buildParams, pagination.current, pagination.pageSize, sorter, onlyOk]);

    // 拉一次下拉选项；日期范围变化时也需重拉（减少不相关的机场/机型）
    useEffect(() => {
        if (!active) return;
        const params = {};
        if (filters?.date_range?.length === 2) {
            params.start_date = filters.date_range[0];
            params.end_date = filters.date_range[1];
        }
        settlementApi.fuelCoverage.getFilterOptions(params)
            .then((response) => {
                const payload = response?.data || {};
                setFilterOptions({
                    departure_airports: payload.departure_airports || [],
                    arrival_airports: payload.arrival_airports || [],
                    aircraft_types: payload.aircraft_types || [],
                });
            })
            .catch((error) => {
                console.error('加载筛选项失败:', error);
            });
    }, [active, filters?.date_range]);

    useEffect(() => {
        if (!active) return;
        setPagination(prev => ({ ...prev, current: 1 }));
        loadData({ page: 1, pageSize: pagination.pageSize });
        // Keep filter changes as the only automatic reload trigger; sorter and current page are user-driven.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, filterSignature]);

    const handleOnlyOkChange = (checked) => {
        setOnlyOk(checked);
        setPagination(prev => ({ ...prev, current: 1 }));
        loadData({ page: 1, pageSize: pagination.pageSize, nextOnlyOk: checked });
    };

    const expandedColumns = [
        { title: '报文类型', dataIndex: 'message_type', key: 'message_type', width: 160 },
        { title: '油单号', dataIndex: 'fuel_slip_number', key: 'fuel_slip_number', width: 140, render: (value) => value || '-' },
        { title: 'OUT t', dataIndex: 'out_fuel_kg', key: 'out_fuel_kg', width: 110, align: 'right', render: (value) => formatTons(value, 2) },
        { title: 'OFF t', dataIndex: 'off_fuel_kg', key: 'off_fuel_kg', width: 110, align: 'right', render: (value) => formatTons(value, 2) },
        { title: 'ON t', dataIndex: 'on_fuel_kg', key: 'on_fuel_kg', width: 110, align: 'right', render: (value) => formatTons(value, 2) },
        { title: 'IN t', dataIndex: 'in_fuel_kg', key: 'in_fuel_kg', width: 110, align: 'right', render: (value) => formatTons(value, 2) },
        { title: '加油 t', dataIndex: 'fuel_uplift_kg', key: 'fuel_uplift_kg', width: 120, align: 'right', render: (value) => formatTons(value, 2) },
        { title: '加油 L', dataIndex: 'fuel_uplift_liters', key: 'fuel_uplift_liters', width: 120, align: 'right', render: (value) => formatNumber(value, 0) },
        { title: '密度', dataIndex: 'fuel_density', key: 'fuel_density', width: 90, align: 'right', render: (value) => formatNumber(value, 4) },
    ];

    const columns = [
        {
            title: '状态',
            dataIndex: 'fuel_status',
            key: 'fuel_status',
            width: 110,
            fixed: 'left',
            align: 'center',
            render: renderStatus,
        },
        {
            title: '航班日期',
            dataIndex: 'flight_date',
            key: 'flight_date',
            sorter: true,
            width: 120,
            fixed: 'left',
            render: formatDate,
        },
        {
            title: '航班号',
            dataIndex: 'flight_number',
            key: 'flight_number',
            sorter: true,
            width: 110,
            fixed: 'left',
            render: (value) => <Text strong>{value || '-'}</Text>,
        },
        {
            title: '飞机号',
            dataIndex: 'aircraft_reg',
            key: 'aircraft_reg',
            sorter: true,
            width: 120,
            render: (value) => value || '-',
        },
        {
            title: '航段',
            key: 'leg',
            width: 120,
            render: (_, record) => `${record.departure_airport || '-'}-${record.arrival_airport || '-'}`,
        },
        {
            title: '空飞',
            dataIndex: 'is_ferry',
            key: 'is_ferry',
            width: 90,
            align: 'center',
            render: (value) => {
                if (value === true) return <Tag color="gold">是</Tag>;
                if (value === false) return <Tag color="green">否</Tag>;
                return <Text type="secondary">-</Text>;
            },
        },
        { title: '航司', dataIndex: 'operator', key: 'operator', width: 100, render: (value) => value || '-' },
        { title: 'BH h', dataIndex: 'bh_hours', key: 'bh_hours', width: 90, align: 'right', render: (value) => formatNumber(value, 2) },
        { title: 'FH h', dataIndex: 'fh_hours', key: 'fh_hours', width: 90, align: 'right', render: (value) => formatNumber(value, 2) },
        { title: '加油 t', dataIndex: 'fuel_uplift_kg_sum', key: 'fuel_uplift_kg_sum', width: 120, align: 'right', render: (value) => formatTons(value, 2) },
        { title: 'BH油耗 T/H', dataIndex: 'block_fuel_kg_per_h', key: 'block_fuel_kg_per_h', width: 130, align: 'right', render: renderTonsPerHour },
        { title: 'FH油耗 T/H', dataIndex: 'flight_fuel_kg_per_h', key: 'flight_fuel_kg_per_h', width: 130, align: 'right', render: renderTonsPerHour },
        { title: 'OUT', dataIndex: 'out', key: 'out', width: 130, render: renderNode },
        { title: 'OFF', dataIndex: 'off', key: 'off', width: 130, render: renderNode },
        { title: 'ON', dataIndex: 'on', key: 'on', width: 130, render: renderNode },
        { title: 'IN', dataIndex: 'in', key: 'in', width: 130, render: renderNode },
        { title: '记录数', dataIndex: 'fuel_report_count', key: 'fuel_report_count', width: 100, align: 'center', render: renderReportCount },
    ];

    const handleTableChange = (nextPagination, _filters, nextSorter) => {
        const normalizedSorter = {
            field: nextSorter?.field,
            order: nextSorter?.order,
        };
        const sorterChanged = normalizedSorter.field !== sorter.field || normalizedSorter.order !== sorter.order;
        setSorter(normalizedSorter);
        loadData({
            page: sorterChanged ? 1 : nextPagination.current,
            pageSize: nextPagination.pageSize,
            nextSorter: normalizedSorter,
        });
    };

    const hasActiveFilter = Boolean(departureAirport) || Boolean(arrivalAirport) || aircraftTypes.length > 0;
    const handleClearFilters = () => {
        setDepartureAirport(null);
        setArrivalAirport(null);
        setAircraftTypes([]);
    };

    return (
        <div style={{ padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
                <Space>
                    <DatabaseOutlined style={{ color: '#1677ff' }} />
                    <Text strong>FOP燃油覆盖列表</Text>
                    <Tooltip title="OOOI时间来自FOP/结算数据，油量来自已匹配燃油报文聚合">
                        <FileSearchOutlined style={{ color: '#8c8c8c' }} />
                    </Tooltip>
                </Space>
                <Space size={12} wrap>
                    <Tooltip title="开启时仅显示数据完整且油耗合理的航班；关闭后展示包含无数据/数据不全/待核查的全部航班，便于结算人员排查问题">
                        <Space size={6}>
                            <Switch size="small" checked={onlyOk} onChange={handleOnlyOkChange} disabled={loading} />
                            <Text>仅显示数据完整的航班</Text>
                        </Space>
                    </Tooltip>
                    <Button icon={<ReloadOutlined />} size="small" onClick={() => loadData()} loading={loading}>
                        刷新
                    </Button>
                    <Button size="small" onClick={() => navigate('/settlement/fuel-reports')}>
                        航班燃油明细
                    </Button>
                </Space>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>筛选：</Text>
                <Select
                    allowClear
                    showSearch
                    placeholder="始发站"
                    style={{ width: 130 }}
                    value={departureAirport}
                    onChange={setDepartureAirport}
                    options={filterOptions.departure_airports.map((code) => ({ value: code, label: code }))}
                    optionFilterProp="label"
                    size="small"
                />
                <Select
                    allowClear
                    showSearch
                    placeholder="目的站"
                    style={{ width: 130 }}
                    value={arrivalAirport}
                    onChange={setArrivalAirport}
                    options={filterOptions.arrival_airports.map((code) => ({ value: code, label: code }))}
                    optionFilterProp="label"
                    size="small"
                />
                <Select
                    mode="multiple"
                    allowClear
                    placeholder="机型（可多选）"
                    style={{ minWidth: 200, maxWidth: 320 }}
                    value={aircraftTypes}
                    onChange={setAircraftTypes}
                    options={filterOptions.aircraft_types.map((t) => ({ value: t, label: t }))}
                    size="small"
                    maxTagCount="responsive"
                />
                {hasActiveFilter && (
                    <Button size="small" type="link" onClick={handleClearFilters}>
                        清空筛选
                    </Button>
                )}
            </div>

            <Spin spinning={loading}>
                <Table
                    rowKey="flight_operation_id"
                    dataSource={rows}
                    columns={columns}
                    size="small"
                    bordered
                    scroll={{ x: 1860 }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条`,
                    }}
                    onChange={handleTableChange}
                    locale={{ emptyText: <Empty description="暂无燃油覆盖数据" /> }}
                    rowClassName={(record) => record.fuel_status === 'anomaly' ? 'fuel-coverage-row-anomaly' : ''}
                    expandable={{
                        rowExpandable: (record) => record.fuel_reports?.length > 0,
                        expandedRowRender: (record) => (
                            <Table
                                rowKey="id"
                                dataSource={record.fuel_reports || []}
                                columns={expandedColumns}
                                size="small"
                                pagination={false}
                                scroll={{ x: 1050 }}
                            />
                        ),
                    }}
                />
            </Spin>
        </div>
    );
};

export default FuelCoveragePanel;
